import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiRequest, { api } from "../API_Service/AxiosConfiguration";

const initialState = {
  token: localStorage.getItem("token") || null,
  user: null,
  roleList: [],
  loading: false,
  error: null,
  changePasswordStatus: null,
  forgotPasswordStatus: null,
  forgotPasswordMessage: null,
  forgotPasswordSentAt: null,
  rolesLoading: false,
};

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (form, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        "POST",
        "Auth/Login",
        {
          UserName: form.email,
          Password: form.password,
          RoleID: form.role,
          RememberMeFlag: form.remember,
        },
        false,
        false,
        false,
        true,
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.post("Auth/RefreshToken");
      if (!res.data?.token) {
        return rejectWithValue({ message: "No token returned" });
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data ?? { message: "Refresh failed" },
      );
    }
  },
);
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { dispatch }) => {
    try {
      await apiRequest("POST", "Auth/Logout", null, false, false, false, true);
    } catch (_) {}
    dispatch(logout());
  },
);

export const getRoleList = createAsyncThunk(
  "auth/getRoleList",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await apiRequest("GET", `Auth/GetRoles?UserID=${userId}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async ({ userId, oldPassword, newPassword }, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        "GET",
        `Auth/ChangePassword?userId=${userId}&oldPassword=${encodeURIComponent(oldPassword)}&newPassword=${encodeURIComponent(newPassword)}`,
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (userName, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        "POST",
        "Auth/ForgotPassword",
        { UserName: userName },
        false,
        false,
        false,
        false,
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data ?? {
          Success: false,
          Message: "An error occurred.",
        },
      );
    }
  },
);
export const ssoExchange = createAsyncThunk(
  "auth/ssoExchange",
  async (code, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        "POST",
        "Auth/SSOExchangeCode",
        { Code: code },
        false,
        false,
        false,
        true,
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

// ── Helper: persist user session to localStorage ──────────────────────────────
function persistSession(token, user) {
  if (!token) return;
  localStorage.setItem("token", token);
  if (user) {
    if (user.CompanyId !== undefined)
      localStorage.setItem("companyId", user.CompanyId);
    if (user.EmployeeID !== undefined)
      localStorage.setItem("empid", user.EmployeeID);
    if (user.EmployeeName !== undefined)
      localStorage.setItem("empName", user.EmployeeName);
    if (user.UserPrivileges !== undefined)
      localStorage.setItem("Privillages", user.UserPrivileges);
    if (user.AllPrivileges !== undefined)
      localStorage.setItem("AllPrivileges", user.AllPrivileges);
  }
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    login: (state, action) => {
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.roleList = action.payload.roleList;
    },
    logout: (state) => {
      state.token = null;
      state.user = null;
      state.roleList = [];
      localStorage.removeItem("token");
    },
    resetChangePasswordStatus: (state) => {
      state.changePasswordStatus = null;
      state.error = null;
    },
    resetForgotPasswordStatus: (state) => {
      state.forgotPasswordStatus = null;
      state.forgotPasswordMessage = null;
      state.forgotPasswordSentAt = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearRoleList: (state) => {
      state.roleList = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // loginUser
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        const userData = data.user;
        if (data?.isUserExists && data?.isRoleExists) {
          state.token = data.token;
          state.user = data.user;
          state.roleList = [];
          localStorage.setItem("token", data.token);
          localStorage.setItem("companyId", userData.CompanyId);
          localStorage.setItem("empid", userData.EmployeeID);
          localStorage.setItem("empName", userData.EmployeeName);
          localStorage.setItem("Privillages", userData.UserPrivileges);
          localStorage.setItem("roleId", userData.RoleID);
          localStorage.setItem("roleName", userData.RoleName);
          localStorage.setItem("roleCode", userData.RoleCode);
          localStorage.setItem("UserId", userData.UserId);
          // Smile integration — 1 = opened from Smile, show "Back to Smile" button
          localStorage.setItem("isFromSmile", String(data.isFromSmile ?? 0));
          localStorage.setItem("smileUrl", data.smileUrl ?? "");
        } else if (data?.isUserExists && !data?.isUserActive) {
          state.error =
            "Your account is inactive. Please contact your administrator.";
        } else if (
          data?.isUserExists &&
          data?.isUserActive &&
          !data?.isPasswordValid
        ) {
          state.error = "Please enter correct password.";
        } else if (data?.isUserExists && !data?.isRoleExists) {
          state.error = "Role not assigned. Please contact your administrator.";
        } else {
          state.error = "Invalid credentials. Please try again.";
        }
      })
      .addCase(loginUser.rejected, (state) => {
        state.loading = false;
        state.error = "Login failed";
      })

      // getRoleList
      .addCase(getRoleList.pending, (state) => {
        state.rolesLoading = true;
      })
      .addCase(getRoleList.fulfilled, (state, action) => {
        state.rolesLoading = true;
        const data = action.payload;

        // New response shape: { roles, isValidUser }
        if (data?.isValidUser === false) {
          state.roleList = [];
          state.error = "Invalid username. Please try again.";
        } else if (Array.isArray(data?.roles)) {
          state.roleList = data.roles;
          state.error = null;
        } else {
          // Fallback: old shape was plain array
          state.roleList = Array.isArray(data) ? data : [];
        }
      })
      .addCase(getRoleList.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to load roles";
      })

      // changePassword
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.changePasswordStatus = null;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        if (payload?.success === true) {
          state.changePasswordStatus = "success";
          state.error = null;
        } else {
          state.changePasswordStatus = "error";
          state.error =
            payload?.message || "Incorrect old password. Please try again.";
        }
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.changePasswordStatus = "error";
        state.error = action.payload || "Failed to change password";
      })
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.forgotPasswordStatus = null;
        state.forgotPasswordMessage = null;
        state.forgotPasswordSentAt = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.Success) {
          state.forgotPasswordStatus = "success";
          state.forgotPasswordSentAt = action.payload.SentDateTime ?? null;
        } else {
          state.forgotPasswordStatus = "error";
          state.forgotPasswordMessage =
            action.payload?.Message ?? "Request failed.";
        }
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.forgotPasswordStatus = "error";
        state.forgotPasswordMessage =
          action.payload?.Message ?? "An error occurred.";
      })

      .addCase(refreshToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.loading = false;
        const token = action.payload?.token ?? null;
        const user = action.payload?.user ?? null;
        if (token) {
          state.token = token;
          state.user = user;
          persistSession(token, user);
        } else {
          state.token = null;
          state.user = null;
          localStorage.removeItem("token");
        }
      })
      .addCase(refreshToken.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.user = null;
        localStorage.removeItem("token");
      })
      .addCase(ssoExchange.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        const userData = data.user;
        if (data?.isUserExists && data?.isRoleExists) {
          state.token = data.token;
          state.user = data.user;
          localStorage.setItem("token", data.token);
          localStorage.setItem("companyId", userData.CompanyId);
          localStorage.setItem("empid", userData.EmployeeID);
          localStorage.setItem("empName", userData.EmployeeName);
          localStorage.setItem("Privillages", userData.UserPrivileges);
          localStorage.setItem("roleId", userData.RoleID);
          localStorage.setItem("roleName", userData.RoleName);
          localStorage.setItem("roleCode", userData.RoleCode);
          localStorage.setItem("UserId", userData.UserId);
          localStorage.setItem("isFromSmile", String(data.isFromSmile ?? 0));
          localStorage.setItem("smileUrl", data.smileUrl ?? "");
        }
      })
      .addCase(ssoExchange.rejected, (state) => {
        state.loading = false;
        state.error = "SSO login failed";
      });
  },
});

export const {
  login,
  logout,
  resetChangePasswordStatus,
  resetForgotPasswordStatus,
  clearError,
  clearRoleList,
} = authSlice.actions;
export default authSlice.reducer;
